import { createStyles, MantineTheme, rem } from "@mantine/core";
import { rgba } from "polished";

export const useStyles = createStyles((theme: MantineTheme) => ({
  loadingCenter: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },

  emptyIcon: {
    opacity: 0.5,
  },

  emptyText: {
    color: theme.colors.dark[8],
  },

  container: {
    padding: rem(16),
    height: "100%",
    overflow: "auto",
    width: "100%",
    maxWidth: "100%",
  },

  fieldLabel: {
    fontWeight: 600,
    color: theme.colors.dark[8],
    marginBottom: rem(6),
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: rem(4),
  },

  editableIndicator: {
    color: theme.colors.dusk[0],
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
  },

  textInputRoot: {
    width: "100%",
    flex: 1,
  },

  textInputField: {
    backgroundColor: theme.colors.gray[1],
    border: `1px solid ${theme.colors.gray[3]}`,
    width: "100%",
  },

  editableField: {
    backgroundColor: rgba(theme.colors.dusk[0], 0.1),
    border: `1px solid ${theme.colors.dusk[0]}`,
    width: "100%",

    "&:focus": {
      backgroundColor: theme.white,
      borderColor: theme.colors.dusk[0],
    },
  },

  readOnlyField: {
    backgroundColor: theme.colors.gray[1],
    border: `1px solid ${theme.colors.gray[3]}`,
    width: "100%",
    cursor: "default",
  },

  changesBanner: {
    backgroundColor: rgba(theme.colors.dusk[0], 0.1),
    border: `1px solid ${theme.colors.dusk[0]}`,
    borderRadius: theme.radius.sm,
    padding: rem(12),
    marginBottom: rem(16),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  changesText: {
    color: theme.colors.dusk[0],
    fontWeight: 500,
  },

  buttonGroup: {
    display: "flex",
    gap: rem(8),
  },

  accordion: {
    "& .mantine-Accordion-control": {
      padding: rem(12),
      backgroundColor: rgba(theme.colors.dusk[0], 0.3),
      borderRadius: theme.radius.sm,

      "&:hover": {
        backgroundColor: rgba(theme.colors.dusk[0], 0.2),
      },
    },

    "& .mantine-Accordion-panel": {
      padding: rem(16),
      paddingTop: rem(12),
    },

    "& .mantine-Accordion-item": {
      border: `1px solid ${theme.colors.gray[3]}`,
      borderRadius: theme.radius.sm,
      overflow: "hidden",

      "&[data-active]": {
        borderColor: theme.colors.dusk[0],
      },
    },
  },

  table: {
    width: "100%",
    maxWidth: "100%",
    fontSize: theme.fontSizes.sm,
    tableLayout: "auto",

    "& th": {
      backgroundColor: theme.colors.gray[1],
      color: theme.colors.dark[7],
      fontWeight: 600,
      padding: rem(12),
      border: `1px solid ${theme.colors.gray[3]}`,
      textAlign: "left",
      wordWrap: "break-word",
    },

    "& td": {
      padding: rem(8),
      border: `1px solid ${theme.colors.gray[3]}`,
      verticalAlign: "top",
      wordBreak: "break-word",
      maxWidth: rem(300),
    },

    "& tbody tr": {
      "&:hover": {
        backgroundColor: theme.colors.gray[0],
      },
    },
  },

  nestedContainer: {
    paddingLeft: rem(16),
    borderLeft: `2px solid ${theme.colors.gray[3]}`,
    marginBottom: rem(16),
    width: "100%",
    maxWidth: "100%",
  },

  arrayItemContainer: {
    padding: rem(12),
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.gray[2]}`,
    marginBottom: rem(8),
    width: "100%",
    maxWidth: "100%",
  },

  arrayItemTitle: {
    fontWeight: 500,
    color: theme.colors.dark[6],
    fontSize: theme.fontSizes.sm,
    marginBottom: rem(8),
  },

  rekeningContainer: {
    gap: rem(24),
    width: "100%",
    maxWidth: "100%",
  },

  basicInfoContainer: {
    gap: rem(16),
    width: "100%",
    maxWidth: "100%",
  },

  fakturContainer: {
    gap: rem(24),
    width: "100%",
    maxWidth: "100%",
  },

  fakturBasicInfo: {
    gap: rem(16),
    width: "100%",
    maxWidth: "100%",
  },

  noDataCenter: {
    width: "100%",
    height: "100%",
  },

  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    container: {
      padding: rem(12),
    },

    fieldContainer: {
      gap: rem(2),
    },

    fieldLabel: {
      fontSize: theme.fontSizes.xs,
    },

    table: {
      fontSize: theme.fontSizes.xs,

      "& th, & td": {
        padding: rem(6),
      },
    },

    changesBanner: {
      flexDirection: "column" as const,
      gap: rem(8),
      alignItems: "stretch" as const,
    },

    buttonGroup: {
      justifyContent: "stretch" as const,

      "& button": {
        flex: 1,
      },
    },
  },
}));
